/* @flow */

import * as React from 'react';
import { inject, observer } from 'mobx-react';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import { List as VirtualList } from 'react-virtualized/dist/commonjs/List';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

//----------------------------------------------------------------------//
// MATERIAL UI                                                          //
//----------------------------------------------------------------------//

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

import type { RowRendererParams } from 'react-virtualized/dist/commonjs/List';

import type {
  TypeManufacturersProductsStore,
  TypePropsProducts,
} from 'stores/ManufacturersProductsStore.js';
import { ComponentWithInjectedProps } from 'utils/injectedProps';

import AddNewLink from 'components/AddNewLink/AddNewLink';
import finderColumns from 'components/FinderColumns/FinderColumns';
import type {
  FinderColumnProps,
  FinderColumnInjectedProps,
} from 'components/FinderColumns/FinderColumns';

import Loading from 'components/Loading/Loading';

import styles from '../ManufacturerList/ManufacturerList.module.scss';

type Props = FinderColumnProps;
type InjectedProps = {
  manufacturersProductsStore: TypeManufacturersProductsStore,
} & TranslatorProps &
  FinderColumnInjectedProps;

@translate()
@inject('manufacturersProductsStore')
@finderColumns
@observer
export default class ProductList extends ComponentWithInjectedProps<Props, {}, InjectedProps> {
  rowRenderer = (vListProps: RowRendererParams) => {
    const { manufacturersProductsStore } = this.props;
    const products = this.props.columnData;
    const row: TypePropsProducts = products[vListProps.index];
    const activeStyle: string =
      vListProps.index === manufacturersProductsStore.selectedActiveListIndex
        ? styles['list-active']
        : '';

    return (
      <ListItem
        key={vListProps.key}
        button={false}
        divider={false}
        className={`${styles['list-item']} ${activeStyle}`}
        style={vListProps.style}
        onClick={(ev: SyntheticInputEvent<*>) =>
          this.handleProductsClick(ev, vListProps, manufacturersProductsStore, row)
        }
      >
        {row.model}
      </ListItem>
    );
  };

  handleProductsClick = (
    ev: SyntheticInputEvent<*>,
    vListProps: RowRendererParams,
    productsStore: TypeManufacturersProductsStore,
    row: TypePropsProducts
  ) => {
    const currentItem: Object = ev.currentTarget;
    const activeItems: Object = currentItem.parentNode.getElementsByClassName(
      styles['list-active']
    );

    [].forEach.call(activeItems, function(el: Object) {
      el.classList.remove(styles['list-active']);
    });

    currentItem.classList.add(styles['list-active']);
    productsStore.selectedActiveListIndex = vListProps.index;

    productsStore.setSelectedProductId(row.id);
  };

  handleAddNewProduct = () => {};

  render(): ?React.Node {
    const { manufacturersProductsStore, t } = this.props;

    const products: any = this.props.columnData;

    if (!manufacturersProductsStore.selectedManufacturer) {
      return null;
    }

    if (!manufacturersProductsStore.selectedProductStatus) {
      return (
        <div className={styles['finder-column-empty']}>
          <p>{t('general:manufacturers.messages.select_detail')}</p>
        </div>
      );
    }

    return manufacturersProductsStore.loadingProducts ? (
      <Loading />
    ) : (
      <React.Fragment>
        <AddNewLink
          linkTo={{
            pathname: '/products/create',
            state: {
              manufacturerId: manufacturersProductsStore.selectedManufacturer.id,
              shortCode: manufacturersProductsStore.selectedManufacturer.short_code,
            },
          }}
          linkText={t('general:actions.add_new_product')}
          permissions={null}
        />
        <List className={styles['expandable-container']} component="div" disablePadding={true}>
          <div className={styles['auto-sizer-wrapper']}>
            <AutoSizer>
              {({ width, height }: Object) => (
                <VirtualList
                  height={height}
                  rowCount={products.length}
                  rowHeight={36}
                  rowRenderer={this.rowRenderer}
                  width={width}
                />
              )}
            </AutoSizer>
          </div>
        </List>
      </React.Fragment>
    );
  }
}
